import * as React from 'react'
// libs
import { ThreadPrimitive } from '@assistant-ui/react'
// icons
import { VerginaSunIcon } from 'components/UI/Icons'
import AssignmentIcon from '@mui/icons-material/Assignment'
// context
import { ChatActionsContext } from '../../ChatRuntimeProvider'
// hooks
import { useConversations } from '../../ConversationsContext'
// components
import { VerginaLoader } from 'components/UI'
import UserMessage from './components/UserMessage'
import ComposerArea from './components/ComposerArea'
import SuggestedPrompts from './components/TemplatesBar'
import AssistantMessage from './components/AssistantMessage'
// views
import {
  EmptyState,
  EmptyStateTitle,
  SummarizeButton,
  StyledThreadRoot,
  EmptyStateSubtitle,
  StyledThreadViewport
} from './views'

interface Props {
  prompts: string[]
}

const Thread: React.FC<Props> = ({ prompts }) => {
  const { context, currentConversationId } = useConversations()
  const { summarise, isSummarising, isLoadingMessages } = React.useContext(ChatActionsContext)

  const isRiskChat = context.type === 'risk'

  // A chat with no conversation behind it is either the very first one or an explicit
  // "New Chat" — the only two cases where the user should be greeted. Anything else is an
  // existing conversation whose messages are simply still on their way.
  const showEmptyState = !currentConversationId

  // The greeting gives way to a loader while the messages of an existing conversation are
  // being fetched, or while the record summary is being generated.
  const showLoader = isSummarising || isLoadingMessages

  return (
    <StyledThreadRoot>
      <StyledThreadViewport>
        <ThreadPrimitive.Empty>
          {showLoader && (
            <EmptyState>
              <VerginaLoader size={96} />
            </EmptyState>
          )}
          {showEmptyState && !showLoader && (
            <EmptyState>
              <VerginaSunIcon
                sx={{
                  fontSize: 80,
                  marginBottom: 4
                }}
              />
              <EmptyStateTitle>Hello there!</EmptyStateTitle>
              <EmptyStateSubtitle>How can I help you today?</EmptyStateSubtitle>
              {isRiskChat ? (
                <SummarizeButton type='button' onClick={() => summarise?.()}>
                  <AssignmentIcon sx={{ fontSize: 24 }} />
                  Summarize
                </SummarizeButton>
              ) : (
                <SuggestedPrompts prompts={prompts} />
              )}
            </EmptyState>
          )}
        </ThreadPrimitive.Empty>
        <ThreadPrimitive.Messages
          components={{
            UserMessage,
            AssistantMessage
          }}
        />
      </StyledThreadViewport>

      <ComposerArea />
    </StyledThreadRoot>
  )
}

export default Thread
